'use client'

import { useRef } from 'react'

import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  getSearchStartData,
  getSearchStartDataFromUrl,
  setSearchSessionData,
} from '@marketplace-web/search/search-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { SortByOption, ItemsState } from '@marketplace-web/catalog/catalog-data'
import {
  endOfListEvent,
  userAppliedOrderEvent,
  userFilterItemsEvent,
  userPaginateCatalogEvent,
  useCatalogContext,
} from '@marketplace-web/catalog/catalog-page-data'

const useItemsTracking = () => {
  const { track } = useTracking()
  const {
    state: {
      filters,
      dynamicFilters: { selectedDynamicFilters },
    },
  } = useCatalogContext()
  const { search_text, search_by_image_uuid: searchByImageUuid } =
    useBrowserNavigation().searchParams
  const prevPage = useRef(0)
  const sessionId = useRef('')

  const trackEndOfList = (state: ItemsState) => {
    track(
      endOfListEvent({
        itemCount: state.pagination?.total_entries || 0,
        page: state.pagination?.current_page,
        perPage: state.pagination?.per_page,
      }),
    )
  }

  const trackUserPaginateCatalog = (state: ItemsState) => {
    track(
      userPaginateCatalogEvent({
        currentPage: state.pagination?.current_page || 0,
        globalSearchSessionId: state.searchTrackingParams?.globalSearchSessionId || null,
        globalCatalogBrowseSessionId:
          state.searchTrackingParams?.globalCatalogBrowseSessionId || null,
        searchCorrelationId: state.searchTrackingParams?.correlationId || '',
        searchSessionId: sessionId.current,
      }),
    )
  }

  const trackUserAppliedOrder = (state: ItemsState) => {
    if (!filters.sortBy) return
    track(
      userAppliedOrderEvent({
        order: filters.sortBy,
        globalSearchSessionId: state.searchTrackingParams?.globalSearchSessionId || null,
        searchSessionId: sessionId.current,
      }),
    )
  }

  const trackUserFilterItems = (state: ItemsState) => {
    const { searchStartId: startId, searchStartType: startType } = getSearchStartData()
    const { searchStartId: startIdFromUrl, searchStartType: startTypeFromUrl } =
      getSearchStartDataFromUrl()
    const searchStartId = startId || startIdFromUrl
    const searchStartType = startType || startTypeFromUrl

    track(
      userFilterItemsEvent({
        filters,
        searchQuery: search_text ? String(search_text) : null,
        searchStartId,
        searchStartType,
        selectedDynamicFilters,
        itemCount: state.pagination?.total_entries || 0,
        globalSearchSessionId: state.searchTrackingParams?.globalSearchSessionId || null,
        searchCorrelationId: state.searchTrackingParams?.correlationId || '',
        globalCatalogBrowseSessionId: state.searchTrackingParams?.globalCatalogBrowseSessionId,
        searchSessionId: sessionId.current,
        searchByImageUuid: searchByImageUuid ? String(searchByImageUuid) : null,
      }),
    )
  }

  return useLatestCallback((state: ItemsState) => {
    // filter items called when items succeeds in same page
    if (!prevPage.current || prevPage.current === state.pagination?.current_page) {
      sessionId.current = state.searchTrackingParams?.sessionId || ''

      trackUserFilterItems(state)
    } else {
      // page change
      trackUserPaginateCatalog(state)
    }
    if (filters.sortBy && filters.sortBy !== SortByOption.Relevance) {
      trackUserAppliedOrder(state)
    }
    if ((state.pagination?.current_page || 0) >= (state.pagination?.total_pages || 0)) {
      trackEndOfList(state)
    }

    setSearchSessionData({
      searchSessionId: sessionId.current,
      globalSearchSessionId: state.searchTrackingParams?.globalSearchSessionId || '',
      globalCatalogBrowseSessionId: state.searchTrackingParams?.globalCatalogBrowseSessionId || '',
    })

    prevPage.current = state.pagination?.current_page || 0
  })
}

export default useItemsTracking
